import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Plus, MapPin, Coins, Edit2, Trash2, X, Save, Search, Layers, Pentagon, Calculator, Play,
} from "lucide-react";
import { MapContainer, TileLayer, Polygon, Marker, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { dataApi, formatApiError } from "../api/client";
import ZonePolygonEditor from "./ZonePolygonEditor";

// Paleta única de 12 colores compartida con LiveMapView. Deterministic hash
// del zone.id → siempre el mismo color en cualquier vista del panel.
const ZONE_PALETTE = [
  "#EF4444", "#F97316", "#F59E0B", "#EAB308",
  "#84CC16", "#10B981", "#06B6D4", "#3B82F6",
  "#6366F1", "#8B5CF6", "#EC4899", "#F43F5E",
];
export function zoneColorFor(id) {
  const s = String(id || "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return ZONE_PALETTE[Math.abs(h) % ZONE_PALETTE.length];
}

const VEHICLE_TYPES = [
  { v: "sedan", l: "Sedán" },
  { v: "hatchback", l: "Hatchback" },
  { v: "suv", l: "SUV" },
  { v: "station_wagon", l: "Station Wagon" },
  { v: "pickup", l: "Pickup" },
  { v: "van", l: "Van" },
];

export default function TariffsView() {
  const [tab, setTab] = useState("zones");
  return (
    <div className="space-y-6 fade-in" data-testid="tariffs-view">
      <header>
        <div className="text-xs font-semibold uppercase tracking-widest text-[#3949AB]">Administración</div>
        <h1 className="font-display font-bold text-2xl">Tarifas y Zonas</h1>
        <p className="text-sm text-neutral-500 mt-1">
          Define zonas de servicio y su tarifario por combinación origen → destino.
        </p>
      </header>

      <div className="border-b border-neutral-200">
        <div className="flex gap-1">
          <TabBtn active={tab === "zones"} onClick={() => setTab("zones")} icon={MapPin} testid="tab-zones">Zonas</TabBtn>
          <TabBtn active={tab === "tariffs"} onClick={() => setTab("tariffs")} icon={Coins} testid="tab-tariffs">Tarifas fijas</TabBtn>
          <TabBtn active={tab === "simulator"} onClick={() => setTab("simulator")} icon={Calculator} testid="tab-simulator">🧮 Simulador</TabBtn>
        </div>
      </div>

      {tab === "zones" ? <ZonesPanel /> : tab === "tariffs" ? <TariffsPanel /> : <PricingSimulator />}
    </div>
  );
}

const TabBtn = ({ active, onClick, icon: Icon, testid, children }) => (
  <button onClick={onClick} data-testid={testid}
    className={`px-4 py-2.5 text-sm font-semibold border-b-2 flex items-center gap-2 transition-colors ${
      active ? "border-[#3949AB] text-[#3949AB]" : "border-transparent text-neutral-500 hover:text-neutral-800"
    }`}>
    <Icon className="w-4 h-4" strokeWidth={2.5}/>{children}
  </button>
);

const ZonesPanel = () => {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [polygonZone, setPolygonZone] = useState(null);
  const [q, setQ] = useState("");

  const load = () => {
    setLoading(true);
    dataApi.zones().then(setZones).catch((e) => setErr(formatApiError(e))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  const filtered = zones.filter((z) => !q || z.name.toLowerCase().includes(q.toLowerCase()) || z.code.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2"/>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar zona..." className="office-input pl-9" data-testid="zones-search"/>
        </div>
        <button className="btn-primary" onClick={() => setShowNew(true)} data-testid="zones-btn-new">
          <Plus className="w-4 h-4" strokeWidth={2.5}/> Nueva zona
        </button>
      </div>
      {err && <div className="chip chip-danger">{err}</div>}
      <div className="office-card overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#F8F9FC] text-xs font-semibold uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="px-4 py-3 text-left">Código</th>
              <th className="px-4 py-3 text-left">Nombre</th>
              <th className="px-4 py-3 text-left">Descripción</th>
              <th className="px-4 py-3 text-right">Base</th>
              <th className="px-4 py-3 text-right">S/ por km</th>
              <th className="px-4 py-3 text-right">Mín. garantizada</th>
              <th className="px-4 py-3 text-center">Polígono</th>
              <th className="px-4 py-3 text-center">Estado</th>
              <th className="px-4 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200">
            {filtered.map((z) => (
              <tr key={z.id} className="hover:bg-neutral-50" data-testid={`zone-row-${z.code}`}>
                <td className="px-4 py-3 font-mono text-xs text-[#3949AB]">
                  <span
                    className="inline-block w-3 h-3 rounded-full mr-2 align-middle"
                    style={{ backgroundColor: zoneColorFor(z.id || z.code), boxShadow: `0 0 0 2px ${zoneColorFor(z.id || z.code)}30` }}
                    data-testid={`zone-color-${z.code}`}
                  />
                  {z.code}
                </td>
                <td className="px-4 py-3 font-medium">{z.name}</td>
                <td className="px-4 py-3 text-sm text-neutral-500">{z.description || "—"}</td>
                <td className="px-4 py-3 text-right font-mono">S/ {z.base_price?.toFixed(2)}</td>
                <td className="px-4 py-3 text-right font-mono">S/ {z.price_per_km?.toFixed(2)}</td>
                <td className="px-4 py-3 text-right font-mono" data-testid={`zone-min-fare-${z.code}`}>
                  {z.min_fare != null ? (
                    <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 px-2 py-0.5 rounded font-bold">
                      🛬 S/ {z.min_fare.toFixed(2)}
                    </span>
                  ) : <span className="text-neutral-300">—</span>}
                </td>
                <td className="px-4 py-3 text-center">
                  {Array.isArray(z.polygon) && z.polygon.length >= 3 ? (
                    <span className="chip chip-success" data-testid={`zone-polygon-status-${z.code}`}>
                      {z.polygon.length} pts
                    </span>
                  ) : (
                    <span className="text-xs text-neutral-400" data-testid={`zone-polygon-status-${z.code}`}>Sin polígono</span>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  {z.is_active ? <span className="chip chip-success">Activa</span> : <span className="chip chip-danger">Inactiva</span>}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button
                    className="btn-ghost !p-1.5"
                    onClick={() => setPolygonZone(z)}
                    title="Dibujar polígono en el mapa"
                    data-testid={`zone-draw-${z.code}`}
                  >
                    <Pentagon className="w-3.5 h-3.5 text-[#3949AB]" strokeWidth={2.5} />
                  </button>
                  <button className="btn-ghost !p-1.5" onClick={() => setEditing(z)} data-testid={`zone-edit-${z.code}`}><Edit2 className="w-3.5 h-3.5" strokeWidth={2.5}/></button>
                  <button className="btn-ghost !p-1.5 !text-[#D13438]" data-testid={`zone-delete-${z.code}`} onClick={async () => {
                    if (!window.confirm(`¿Eliminar zona ${z.name}?`)) return;
                    try { await dataApi.deleteZone(z.id); load(); } catch (e) { alert(formatApiError(e)); }
                  }}><Trash2 className="w-3.5 h-3.5" strokeWidth={2.5}/></button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-neutral-500 text-sm">
                {loading ? "Cargando zonas..." : zones.length === 0 ? "Aún no hay zonas registradas." : "Sin resultados."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      {(showNew || editing) && (
        <ZoneFormModal zone={editing}
          onClose={() => { setShowNew(false); setEditing(null); }}
          onSaved={() => { setShowNew(false); setEditing(null); load(); }}/>
      )}
      {polygonZone && (
        <ZonePolygonEditor
          zone={polygonZone}
          onClose={() => setPolygonZone(null)}
          onSaved={() => { setPolygonZone(null); load(); }}
        />
      )}
    </div>
  );
};

const ZoneFormModal = ({ zone, onClose, onSaved }) => {
  const isEdit = !!zone;
  const [form, setForm] = useState({
    code: zone?.code || "",
    name: zone?.name || "",
    description: zone?.description || "",
    base_price: zone?.base_price ?? 5,
    price_per_km: zone?.price_per_km ?? 2,
    min_fare: zone?.min_fare ?? null,
    is_active: zone?.is_active ?? true,
  });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setSaving(true);
    try { if (isEdit) await dataApi.updateZone(zone.id, form); else await dataApi.createZone(form); onSaved(); }
    catch (e) { setErr(formatApiError(e)); } finally { setSaving(false); }
  };
  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose}></div>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-6" onClick={onClose}>
        <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-lg shadow-xl max-w-lg w-full" data-testid="zone-form-modal">
          <div className="border-b px-5 py-3 flex items-center justify-between">
            <h3 className="font-display font-semibold text-lg">{isEdit ? "Editar zona" : "Nueva zona"}</h3>
            <button type="button" onClick={onClose} className="btn-ghost !p-1.5"><X className="w-4 h-4" strokeWidth={2.5}/></button>
          </div>
          <div className="p-5 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Código *</label>
                <input value={form.code} disabled={isEdit} onChange={(e) => setForm({...form, code: e.target.value.toUpperCase()})}
                  className="office-input mt-1.5 font-mono uppercase" placeholder="Z-URB" required data-testid="zone-input-code"/>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Nombre *</label>
                <input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                  className="office-input mt-1.5" placeholder="Zona Urbana" required data-testid="zone-input-name"/>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Descripción</label>
              <textarea value={form.description || ""} onChange={(e) => setForm({...form, description: e.target.value})}
                className="office-input mt-1.5" rows={2} data-testid="zone-input-desc"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Tarifa base (S/)</label>
                <input type="number" step="0.5" min="0" value={form.base_price}
                  onChange={(e) => setForm({...form, base_price: parseFloat(e.target.value) || 0})}
                  className="office-input mt-1.5 font-mono" data-testid="zone-input-base"/>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Precio por km (S/)</label>
                <input type="number" step="0.1" min="0" value={form.price_per_km}
                  onChange={(e) => setForm({...form, price_per_km: parseFloat(e.target.value) || 0})}
                  className="office-input mt-1.5 font-mono" data-testid="zone-input-km"/>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500 flex items-center gap-1.5">
                🛬 Tarifa mínima garantizada (S/)
                <span className="text-[9px] font-normal normal-case tracking-normal text-neutral-400">Aeropuerto/Terminal</span>
              </label>
              <input type="number" step="0.5" min="0"
                value={form.min_fare ?? ""}
                onChange={(e) => setForm({...form, min_fare: e.target.value === "" ? null : parseFloat(e.target.value)})}
                placeholder="Dejar vacío = sin mínimo (usar cálculo dinámico)"
                className="office-input mt-1.5 font-mono" data-testid="zone-input-min-fare"/>
              <div className="text-[11px] text-neutral-500 mt-1">
                Si el cálculo dinámico (base + km × precio_km) queda por debajo de este monto, se cobra esta tarifa. Deja vacío para zonas normales.
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({...form, is_active: e.target.checked})} data-testid="zone-input-active"/>
              Zona activa
            </label>
            {err && <div className="chip chip-danger">{err}</div>}
          </div>
          <div className="border-t px-5 py-3 flex justify-end gap-2 bg-neutral-50">
            <button type="button" onClick={onClose} className="btn-secondary">Cancelar</button>
            <button type="submit" disabled={saving} className="btn-primary" data-testid="zone-submit">
              <Save className="w-3.5 h-3.5" strokeWidth={2.5}/>{saving ? "Guardando..." : (isEdit ? "Guardar" : "Crear zona")}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

const TariffsPanel = () => {
  const [tariffs, setTariffs] = useState([]);
  const [zones, setZones] = useState([]);
  const [err, setErr] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try { const [t, z] = await Promise.all([dataApi.tariffs(), dataApi.zones()]); setTariffs(t); setZones(z); }
    catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm text-neutral-500">
          <Layers className="inline w-4 h-4 mr-1"/>{tariffs.length} tarifas · Origen · Destino · Tipo
        </div>
        <button className="btn-primary" onClick={() => setShowNew(true)} data-testid="tariffs-btn-new">
          <Plus className="w-4 h-4" strokeWidth={2.5}/> Nueva tarifa
        </button>
      </div>
      {err && <div className="chip chip-danger">{err}</div>}
      <div className="office-card overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#F8F9FC] text-xs font-semibold uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="px-4 py-3 text-left">Origen</th>
              <th className="px-4 py-3 text-left">Destino</th>
              <th className="px-4 py-3 text-left">Tipo</th>
              <th className="px-4 py-3 text-right">Precio fijo</th>
              <th className="px-4 py-3 text-center">Estado</th>
              <th className="px-4 py-3 text-left">Notas</th>
              <th className="px-4 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200">
            {tariffs.map((t) => (
              <tr key={t.id} className="hover:bg-neutral-50" data-testid={`tariff-row-${t.id}`}>
                <td className="px-4 py-3 text-sm">{t.origin_zone_name}</td>
                <td className="px-4 py-3 text-sm">{t.dest_zone_name}</td>
                <td className="px-4 py-3 text-sm"><span className="chip chip-info">{VEHICLE_TYPES.find((v) => v.v === t.vehicle_type)?.l || t.vehicle_type}</span></td>
                <td className="px-4 py-3 text-right font-mono font-semibold text-[#3949AB]">S/ {t.fixed_price?.toFixed(2)}</td>
                <td className="px-4 py-3 text-center">{t.is_active ? <span className="chip chip-success">Activa</span> : <span className="chip chip-danger">Inactiva</span>}</td>
                <td className="px-4 py-3 text-xs text-neutral-500">{t.notes || "—"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button className="btn-ghost !p-1.5" onClick={() => setEditing(t)}><Edit2 className="w-3.5 h-3.5" strokeWidth={2.5}/></button>
                  <button className="btn-ghost !p-1.5 !text-[#D13438]" onClick={async () => {
                    if (!window.confirm("¿Eliminar tarifa?")) return;
                    try { await dataApi.deleteTariff(t.id); load(); } catch (e) { alert(formatApiError(e)); }
                  }}><Trash2 className="w-3.5 h-3.5" strokeWidth={2.5}/></button>
                </td>
              </tr>
            ))}
            {tariffs.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-neutral-500 text-sm">
                Sin tarifas fijas registradas. Se usará el precio base de la zona destino.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      {(showNew || editing) && (
        <TariffFormModal tariff={editing} zones={zones}
          onClose={() => { setShowNew(false); setEditing(null); }}
          onSaved={() => { setShowNew(false); setEditing(null); load(); }}/>
      )}
    </div>
  );
};

const TariffFormModal = ({ tariff, zones, onClose, onSaved }) => {
  const isEdit = !!tariff;
  const [form, setForm] = useState({
    origin_zone_id: tariff?.origin_zone_id || (zones[0]?.id || ""),
    dest_zone_id: tariff?.dest_zone_id || (zones[1]?.id || zones[0]?.id || ""),
    vehicle_type: tariff?.vehicle_type || "sedan",
    fixed_price: tariff?.fixed_price ?? 10,
    notes: tariff?.notes || "",
    is_active: tariff?.is_active ?? true,
  });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setSaving(true);
    try {
      if (isEdit) await dataApi.updateTariff(tariff.id, { fixed_price: form.fixed_price, notes: form.notes, is_active: form.is_active });
      else await dataApi.createTariff(form);
      onSaved();
    } catch (e) { setErr(formatApiError(e)); } finally { setSaving(false); }
  };
  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose}></div>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-6" onClick={onClose}>
        <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-lg shadow-xl max-w-lg w-full" data-testid="tariff-form-modal">
          <div className="border-b px-5 py-3 flex items-center justify-between">
            <h3 className="font-display font-semibold text-lg">{isEdit ? "Editar tarifa" : "Nueva tarifa"}</h3>
            <button type="button" onClick={onClose} className="btn-ghost !p-1.5"><X className="w-4 h-4" strokeWidth={2.5}/></button>
          </div>
          <div className="p-5 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Origen *</label>
                <select value={form.origin_zone_id} disabled={isEdit} onChange={(e) => setForm({...form, origin_zone_id: e.target.value})} className="office-input mt-1.5" data-testid="tariff-origin">
                  {zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Destino *</label>
                <select value={form.dest_zone_id} disabled={isEdit} onChange={(e) => setForm({...form, dest_zone_id: e.target.value})} className="office-input mt-1.5" data-testid="tariff-dest">
                  {zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Tipo vehículo *</label>
                <select value={form.vehicle_type} disabled={isEdit} onChange={(e) => setForm({...form, vehicle_type: e.target.value})} className="office-input mt-1.5" data-testid="tariff-vehicle">
                  {VEHICLE_TYPES.map((v) => <option key={v.v} value={v.v}>{v.l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Precio fijo (S/)</label>
                <input type="number" step="0.5" min="0" value={form.fixed_price}
                  onChange={(e) => setForm({...form, fixed_price: parseFloat(e.target.value) || 0})}
                  className="office-input mt-1.5 font-mono" required data-testid="tariff-price"/>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Notas</label>
              <textarea value={form.notes || ""} onChange={(e) => setForm({...form, notes: e.target.value})}
                className="office-input mt-1.5" rows={2} data-testid="tariff-notes"/>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({...form, is_active: e.target.checked})}/>
              Tarifa activa
            </label>
            {err && <div className="chip chip-danger">{err}</div>}
          </div>
          <div className="border-t px-5 py-3 flex justify-end gap-2 bg-neutral-50">
            <button type="button" onClick={onClose} className="btn-secondary">Cancelar</button>
            <button type="submit" disabled={saving} className="btn-primary" data-testid="tariff-submit">
              <Save className="w-3.5 h-3.5" strokeWidth={2.5}/>{saving ? "Guardando..." : (isEdit ? "Guardar" : "Crear tarifa")}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

// ═════════════════════════════════════════════════════════════════════════
// 🧮 SIMULADOR DE PRECIOS — Cotización en vivo con motor híbrido
// ═════════════════════════════════════════════════════════════════════════
function PricingSimulator() {
  // Puerto Maldonado default (Plaza de Armas → Aeropuerto)
  const [oLat, setOLat] = useState("-12.5960");
  const [oLng, setOLng] = useState("-69.1890");
  const [dLat, setDLat] = useState("-12.6136");
  const [dLng, setDLng] = useState("-69.2286");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [zones, setZones] = useState([]);

  useEffect(() => {
    dataApi.zones().then(setZones).catch(() => {});
  }, []);

  const run = async () => {
    setBusy(true); setErr(""); setResult(null);
    try {
      const r = await dataApi.quotePrice({
        origin_lat: parseFloat(oLat), origin_lng: parseFloat(oLng),
        dest_lat: parseFloat(dLat), dest_lng: parseFloat(dLng),
      });
      setResult(r);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const swap = () => { setOLat(dLat); setOLng(dLng); setDLat(oLat); setDLng(oLng); };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="pricing-simulator">
      {/* Inputs */}
      <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <Calculator className="w-5 h-5 text-[#3949AB]" />
          <div className="font-bold text-lg">Cotiza un viaje</div>
        </div>
        <div className="text-xs text-neutral-500 mb-4">
          Prueba cómo el motor calcula tarifas antes de aplicar cambios. Usa coords reales del origen y destino o <b>arrastra los pines</b> en el mapa.
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">📍 Origen (lat, lng)</label>
          <div className="grid grid-cols-2 gap-2 mt-1.5">
            <input value={oLat} onChange={(e) => setOLat(e.target.value)} className="office-input font-mono text-sm" placeholder="-12.5960" data-testid="sim-origin-lat"/>
            <input value={oLng} onChange={(e) => setOLng(e.target.value)} className="office-input font-mono text-sm" placeholder="-69.1890" data-testid="sim-origin-lng"/>
          </div>
        </div>

        <div className="flex justify-center">
          <button onClick={swap} className="text-xs text-[#3949AB] font-semibold hover:underline" data-testid="sim-swap-btn">⇅ Intercambiar</button>
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">🏁 Destino (lat, lng)</label>
          <div className="grid grid-cols-2 gap-2 mt-1.5">
            <input value={dLat} onChange={(e) => setDLat(e.target.value)} className="office-input font-mono text-sm" placeholder="-12.6136" data-testid="sim-dest-lat"/>
            <input value={dLng} onChange={(e) => setDLng(e.target.value)} className="office-input font-mono text-sm" placeholder="-69.2286" data-testid="sim-dest-lng"/>
          </div>
        </div>

        <button onClick={run} disabled={busy}
                className="w-full mt-4 py-3 bg-[#3949AB] text-white rounded-lg font-bold flex items-center justify-center gap-2 hover:bg-[#303F9F] disabled:opacity-50" data-testid="sim-run-btn">
          <Play className="w-4 h-4" /> {busy ? "Calculando..." : "Calcular tarifa"}
        </button>

        {err && <div className="chip chip-danger mt-2">{err}</div>}

        <div className="mt-3 grid grid-cols-2 gap-2">
          <button onClick={() => { setOLat("-12.6136"); setOLng("-69.2286"); setDLat("-12.5960"); setDLng("-69.1890"); }}
                  className="text-xs px-3 py-2 border rounded-lg font-semibold hover:bg-neutral-50">
            🛬 Aeropuerto → Centro
          </button>
          <button onClick={() => { setOLat("-12.5960"); setOLng("-69.1890"); setDLat("-12.5850"); setDLng("-69.1820"); }}
                  className="text-xs px-3 py-2 border rounded-lg font-semibold hover:bg-neutral-50">
            🏘️ Corto urbano
          </button>
        </div>

        {/* Mapa visual del simulador */}
        <div className="mt-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-2 flex items-center gap-1">
            🗺️ Vista del recorrido
            {result?.breakdown?.traversed_zone_ids?.length > 0 && (
              <span className="text-purple-700 normal-case tracking-normal">
                · {result.breakdown.traversed_zone_ids.length} polígono{result.breakdown.traversed_zone_ids.length > 1 ? "s" : ""} atravesado{result.breakdown.traversed_zone_ids.length > 1 ? "s" : ""}
              </span>
            )}
          </div>
          <SimulatorMap
            oLat={oLat} oLng={oLng} dLat={dLat} dLng={dLng}
            setOLat={setOLat} setOLng={setOLng} setDLat={setDLat} setDLng={setDLng}
            zones={zones}
            traversedIds={result?.breakdown?.traversed_zone_ids || []}
            originZoneId={result?.breakdown?.origin_zone_id}
            routeGeometry={result?.breakdown?.route_geometry}
          />
          <div className="mt-2 text-[10px] text-neutral-500 flex flex-wrap items-center gap-3">
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-1 rounded bg-emerald-500"/> km internos (gratis)</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-1 rounded bg-red-500"/> km externos (cobrados)</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded border-2 border-purple-600 bg-purple-200/50"/> polígono atravesado (peaje)</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded border border-neutral-300 bg-neutral-100"/> polígono no cruzado</span>
          </div>
        </div>
      </div>

      {/* Result */}
      <div>
        {!result ? (
          <div className="bg-neutral-50 border-2 border-dashed border-neutral-300 rounded-xl p-10 text-center text-neutral-400 h-full flex items-center justify-center">
            <div>
              <Calculator className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <div className="text-sm font-medium">Ingresa origen y destino, luego &quot;Calcular tarifa&quot;</div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Final fare card */}
            <div className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-xl p-6 text-center shadow-lg" data-testid="sim-result-final">
              <div className="text-xs uppercase tracking-widest opacity-80">Tarifa final</div>
              <div className="text-5xl font-black mt-1">S/ {result.final_fare.toFixed(2)}</div>
              {result.breakdown.min_fare_applied && (
                <div className="mt-2 inline-block bg-white/20 text-xs font-bold px-3 py-1 rounded-full">
                  🛬 MÍNIMA GARANTIZADA APLICADA
                </div>
              )}
              {result.breakdown.pricing_band_name && (
                <div className="mt-2 inline-block bg-white/20 text-xs font-bold px-3 py-1 rounded-full ml-1">
                  ⚡ {result.breakdown.pricing_band_name} ×{result.breakdown.pricing_multiplier}
                </div>
              )}
            </div>

            {/* Breakdown */}
            <div className="bg-white border border-neutral-200 rounded-xl p-5">
              <div className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-3">Cálculo paso a paso</div>
              <BreakdownRow label="Zona origen" value={result.breakdown.origin_zone_name || "—"} />
              <BreakdownRow label="Zona destino" value={result.breakdown.dest_zone_name || "—"} />
              <BreakdownRow
                label={`Base Origen${result.breakdown.origin_base_label ? ` (${result.breakdown.origin_base_label})` : ""}`}
                value={`S/ ${(result.breakdown.origin_base ?? result.breakdown.base_price_used).toFixed(2)}`}
                highlight
              />

              {/* 🚦 Cargos por tránsito de polígonos */}
              {Array.isArray(result.breakdown.transit_zones) && result.breakdown.transit_zones.length > 0 && (
                <>
                  <div className="border-t border-dashed my-2"></div>
                  <div className="text-[11px] font-bold uppercase tracking-wider text-purple-700 mt-1 mb-0.5 flex items-center gap-1">
                    🚦 Cargo por Tránsito de Polígonos
                  </div>
                  {result.breakdown.transit_zones.map((tz) => (
                    <BreakdownRow
                      key={tz.zone_id}
                      label={`↳ ${tz.name}`}
                      value={`+ S/ ${tz.base_price.toFixed(2)}`}
                    />
                  ))}
                  <BreakdownRow
                    label="Subtotal tránsito"
                    value={`S/ ${result.breakdown.transit_total.toFixed(2)}`}
                    highlight
                  />
                </>
              )}

              <div className="border-t border-dashed my-2"></div>
              <BreakdownRow
                label={result.breakdown.distance_source === "osrm" ? "🛣️ Distancia por carretera (OSRM)" : "📏 Distancia (fallback línea recta)"}
                value={`${result.breakdown.distance_km.toFixed(2)} km`}
              />
              {result.breakdown.distance_source === "osrm" && (
                <BreakdownRow
                  label="↳ (línea recta como referencia)"
                  value={`${result.breakdown.distance_linear_km.toFixed(2)} km`}
                />
              )}
              <BreakdownRow
                label="↳ km internos (no se cobran)"
                value={`${(result.breakdown.km_internal ?? 0).toFixed(2)} km`}
              />
              <BreakdownRow
                label="↳ km externos (facturables)"
                value={`${(result.breakdown.km_external ?? 0).toFixed(2)} km`}
              />
              <BreakdownRow label="Precio por km" value={`S/ ${result.breakdown.price_per_km.toFixed(2)}`} />
              <BreakdownRow
                label="Recargo KM externos"
                value={`+ S/ ${(result.breakdown.km_external_charge ?? 0).toFixed(2)}`}
                formula={`${(result.breakdown.km_external ?? 0).toFixed(2)} × ${result.breakdown.price_per_km.toFixed(2)}`}
              />

              <div className="border-t border-dashed my-2"></div>
              <BreakdownRow
                label="Cálculo dinámico"
                value={`S/ ${result.breakdown.dynamic_calc.toFixed(2)}`}
                formula={`Base ${(result.breakdown.origin_base ?? result.breakdown.base_price_used).toFixed(2)}${(result.breakdown.transit_total ?? 0) > 0 ? ` + Tránsito ${result.breakdown.transit_total.toFixed(2)}` : ""} + KM ${(result.breakdown.km_external_charge ?? 0).toFixed(2)}`}
              />
              {(result.breakdown.min_fare_origin > 0 || result.breakdown.min_fare_dest > 0) && (
                <BreakdownRow label="Tarifa mínima garantizada" value={`S/ ${Math.max(result.breakdown.min_fare_origin, result.breakdown.min_fare_dest).toFixed(2)}`} highlight={result.breakdown.min_fare_applied} />
              )}
              <div className="border-t border-dashed my-2"></div>
              <BreakdownRow label="Pre-banda" value={`S/ ${result.breakdown.pre_band_fare.toFixed(2)}`} />
              {result.breakdown.pricing_band_name && (
                <BreakdownRow label={`Banda: ${result.breakdown.pricing_band_name}`} value={`×${result.breakdown.pricing_multiplier}`} />
              )}
              <BreakdownRow label="TOTAL" value={`S/ ${result.final_fare.toFixed(2)}`} bold big />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function BreakdownRow({ label, value, formula, highlight, bold, big }) {
  return (
    <div className={`flex items-baseline justify-between py-1.5 ${bold ? "border-t border-neutral-200 pt-2 mt-1" : ""}`}>
      <div className={`text-sm ${highlight ? "text-amber-700 font-bold" : ""} ${bold ? "font-black" : ""}`}>{label}</div>
      <div className="text-right">
        {formula && <div className="text-[10px] text-neutral-400 font-mono">{formula}</div>}
        <div className={`font-mono ${bold ? "font-black" : "font-semibold"} ${big ? "text-xl text-emerald-700" : "text-sm"} ${highlight ? "text-amber-700" : ""}`}>{value}</div>
      </div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════
// 🗺️ Mapa del Simulador — muestra polígonos, línea origen→destino,
// segmentos internos (verde) vs externos (rojo), y resalta los polígonos
// que la trayectoria atraviesa (peaje).
// ═════════════════════════════════════════════════════════════════════
function _makeIcon(colorHex, letter) {
  const html = `
    <div style="
      background:${colorHex};
      width:28px;height:28px;border-radius:50% 50% 50% 0;
      transform:rotate(-45deg);
      border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,.35);
      display:flex;align-items:center;justify-content:center;
    "><span style="transform:rotate(45deg);color:white;font-weight:900;font-size:12px;">${letter}</span></div>`;
  return L.divIcon({ html, iconSize: [28, 28], iconAnchor: [14, 28], className: "" });
}
const ORIGIN_ICON = _makeIcon("#10B981", "A");
const DEST_ICON = _makeIcon("#EF4444", "B");

function _pointInPolygon(lat, lng, poly) {
  // Ray casting; poly = [[lat,lng], ...]
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [yi, xi] = poly[i];
    const [yj, xj] = poly[j];
    const intersect = ((yi > lat) !== (yj > lat)) &&
      (lng < ((xj - xi) * (lat - yi)) / ((yj - yi) || 1e-12) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

function _buildSegments(oLat, oLng, dLat, dLng, urbanZones) {
  // Muestreo 200+1 puntos → clasifica dentro/fuera → agrupa en tramos.
  const N = 200;
  const points = [];
  for (let i = 0; i <= N; i++) {
    const t = i / N;
    const lat = oLat + (dLat - oLat) * t;
    const lng = oLng + (dLng - oLng) * t;
    let inside = false;
    for (const z of urbanZones) {
      if (_pointInPolygon(lat, lng, z.polygon)) { inside = true; break; }
    }
    points.push([lat, lng, inside]);
  }
  const segments = [];
  let cur = { inside: points[0][2], pts: [[points[0][0], points[0][1]]] };
  for (let i = 1; i < points.length; i++) {
    const [la, ln, ins] = points[i];
    if (ins === cur.inside) {
      cur.pts.push([la, ln]);
    } else {
      cur.pts.push([la, ln]);
      segments.push(cur);
      cur = { inside: ins, pts: [[la, ln]] };
    }
  }
  segments.push(cur);
  return segments;
}

function _FitBounds({ oLat, oLng, dLat, dLng, zones }) {
  const map = useMap();
  useEffect(() => {
    try {
      const pts = [[oLat, oLng], [dLat, dLng]];
      zones.forEach((z) => (z.polygon || []).forEach((p) => pts.push([p[0], p[1]])));
      if (pts.length >= 2) map.fitBounds(pts, { padding: [30, 30], maxZoom: 14 });
    } catch { /* noop */ }
  }, [oLat, oLng, dLat, dLng, zones.length]); // eslint-disable-line
  return null;
}

function SimulatorMap({ oLat, oLng, dLat, dLng, setOLat, setOLng, setDLat, setDLng, zones, traversedIds, originZoneId, routeGeometry }) {
  const origin = [parseFloat(oLat) || -12.596, parseFloat(oLng) || -69.189];
  const dest = [parseFloat(dLat) || -12.6136, parseFloat(dLng) || -69.2286];
  const urbanZones = useMemo(() =>
    (zones || []).filter((z) => Array.isArray(z.polygon) && z.polygon.length >= 3 && z.is_active),
  [zones]);
  // Usar la geometría OSRM del backend si está disponible; si no, línea recta.
  const routePts = useMemo(() => {
    if (Array.isArray(routeGeometry) && routeGeometry.length >= 2) return routeGeometry;
    return [[origin[0], origin[1]], [dest[0], dest[1]]];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeGeometry, origin[0], origin[1], dest[0], dest[1]]);
  const segments = useMemo(() => {
    // Segmenta la ruta real en tramos internos/externos.
    if (routePts.length < 2) return [];
    const segs = [];
    const _inside = (lat, lng) => {
      for (const z of urbanZones) if (_pointInPolygon(lat, lng, z.polygon)) return true;
      return false;
    };
    let cur = { inside: _inside(routePts[0][0], routePts[0][1]), pts: [routePts[0]] };
    for (let i = 1; i < routePts.length; i++) {
      const p = routePts[i];
      const ins = _inside(p[0], p[1]);
      cur.pts.push(p);
      if (ins !== cur.inside) {
        segs.push(cur);
        cur = { inside: ins, pts: [p] };
      }
    }
    segs.push(cur);
    return segs;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routePts, urbanZones]);

  const traversedSet = new Set(traversedIds || []);

  return (
    <div className="rounded-lg overflow-hidden border border-neutral-300" style={{ height: 360 }}>
      <MapContainer center={origin} zoom={13} style={{ height: "100%", width: "100%" }} scrollWheelZoom
                    data-testid="sim-map">
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; OpenStreetMap'
        />
        <_FitBounds oLat={origin[0]} oLng={origin[1]} dLat={dest[0]} dLng={dest[1]} zones={urbanZones} />

        {/* Polígonos */}
        {urbanZones.map((z) => {
          const crossed = traversedSet.has(z.id);
          const isOrigin = originZoneId === z.id;
          const color = crossed ? (isOrigin ? "#10B981" : "#8B5CF6") : (zoneColorFor(z.id) + "88");
          return (
            <Polygon
              key={z.id}
              positions={(z.polygon || []).map((p) => [p[0], p[1]])}
              pathOptions={{
                color: crossed ? color : "#9CA3AF",
                weight: crossed ? 3 : 1.5,
                fillColor: color,
                fillOpacity: crossed ? 0.32 : 0.08,
                dashArray: crossed ? undefined : "4 4",
              }}
            />
          );
        })}

        {/* Segmentos coloreados */}
        {segments.map((s, i) => (
          <Polyline
            key={i}
            positions={s.pts}
            pathOptions={{
              color: s.inside ? "#10B981" : "#EF4444",
              weight: 5,
              opacity: 0.9,
            }}
          />
        ))}

        {/* Pines origen / destino */}
        <Marker
          position={origin}
          icon={ORIGIN_ICON}
          draggable
          eventHandlers={{
            dragend: (e) => {
              const p = e.target.getLatLng();
              setOLat(p.lat.toFixed(6));
              setOLng(p.lng.toFixed(6));
            },
          }}
        />
        <Marker
          position={dest}
          icon={DEST_ICON}
          draggable
          eventHandlers={{
            dragend: (e) => {
              const p = e.target.getLatLng();
              setDLat(p.lat.toFixed(6));
              setDLng(p.lng.toFixed(6));
            },
          }}
        />
      </MapContainer>
    </div>
  );
}

